import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { areValuesEqual } from '@utils/settings.util'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  EmailSendAsSettings,
  EmailSettingsData,
  EmailVacationSettings,
  getEmailMutationErrorMessage,
  getEmailSettingsErrorMessage,
  getGoogleWorkspaceErrorMessage,
  isEmailBackendEnabled,
  requestCreateEmailLabel,
  requestDeleteEmailLabel,
  requestEmailLabels,
  requestEmailSendAs,
  requestEmailSettings,
  requestEmailVacation,
  requestImportGoogleBackedEmailSettings,
  requestGoogleWorkspaceStatus,
  requestUpdateEmailLabel,
  requestUpdateEmailSendAs,
  requestUpdateEmailSettings,
  requestUpdateEmailVacation,
} from '@services/api/email.api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useClinicChange from '../shared/useClinicChange'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { resetEmailState } from '@store/reducers/email.reducer'

const LOADING_CONTAINER = 'settings-email'

export type EmailTab = 'general' | 'inbox' | 'tags' | 'advanced'

export type EmailTag = {
  id: string
  name: string
  color: string
}

export type EmailInboxSettings = {
  show_images: 'ask' | 'always' | 'never'
  notify_new_email: boolean
  show_unread_mail_indicator: boolean
  confirm_link_urls: boolean
}

export type EmailAdvancedSettings = {
  automatic_replies_enabled: boolean
  from_datetime: string
  to_datetime: string
  subject: string
  message: string
}

export type EmailSettingsForm = {
  profile_picture_url: string | null
  display_name: string
  grammar_suggestions: boolean
  spelling_suggestions: boolean
  autocorrect_support: boolean
  signature_enabled: boolean
  signature: string
  inbox: EmailInboxSettings
  tags: EmailTag[]
  advanced: EmailAdvancedSettings
}

const DEFAULT_ADVANCED_SETTINGS: EmailAdvancedSettings = {
  automatic_replies_enabled: true,
  from_datetime: new Date(2026, 3, 8, 8, 0, 0).toISOString(),
  to_datetime: new Date(2026, 3, 10, 18, 0, 0).toISOString(),
  subject: '',
  message: '',
}

const DISCONNECTED_ADVANCED_SETTINGS: EmailAdvancedSettings = {
  automatic_replies_enabled: false,
  from_datetime: '',
  to_datetime: '',
  subject: '',
  message: '',
}

const DEFAULT_INBOX_SETTINGS: EmailInboxSettings = {
  show_images: 'ask',
  notify_new_email: true,
  show_unread_mail_indicator: true,
  confirm_link_urls: true,
}

const DEFAULT_SETTINGS: EmailSettingsForm = {
  profile_picture_url: null,
  display_name: '',
  grammar_suggestions: true,
  spelling_suggestions: true,
  autocorrect_support: true,
  signature_enabled: true,
  signature: '',
  inbox: DEFAULT_INBOX_SETTINGS,
  tags: [],
  advanced: DEFAULT_ADVANCED_SETTINGS,
}

const flattenForm = (
  form: EmailSettingsForm,
): (string | boolean | number)[] => [
  form.profile_picture_url ?? '',
  form.display_name,
  form.grammar_suggestions,
  form.spelling_suggestions,
  form.autocorrect_support,
  form.signature_enabled,
  form.signature,
  form.inbox.show_images,
  form.inbox.notify_new_email,
  form.inbox.show_unread_mail_indicator,
  form.inbox.confirm_link_urls,
  JSON.stringify(form.tags),
  form.advanced.automatic_replies_enabled,
  form.advanced.from_datetime,
  form.advanced.to_datetime,
  form.advanced.subject,
  form.advanced.message,
]

const applySettings = (
  form: EmailSettingsForm,
  settings: EmailSettingsData,
): EmailSettingsForm => ({
  ...form,
  profile_picture_url: settings.profile_picture_url ?? null,
  display_name: settings.display_name ?? '',
  grammar_suggestions: settings.grammar_suggestions ?? true,
  spelling_suggestions: settings.spelling_suggestions ?? true,
  autocorrect_support: settings.autocorrect_support,
  signature_enabled: settings.signature_enabled,
  signature: settings.signature ?? '',
  inbox: {
    show_images: settings.show_images,
    notify_new_email: settings.notify_new_email,
    show_unread_mail_indicator: settings.show_unread_mail_indicator,
    confirm_link_urls: settings.confirm_link_urls,
  },
})

const toSettingsPayload = (
  form: EmailSettingsForm,
): Partial<EmailSettingsData> => ({
  display_name: form.display_name.trim() ? form.display_name : null,
  grammar_suggestions: form.grammar_suggestions,
  spelling_suggestions: form.spelling_suggestions,
  autocorrect_support: form.autocorrect_support,
  notify_new_email: form.inbox.notify_new_email,
  notify_email_reply: form.inbox.notify_new_email,
  signature_enabled: form.signature_enabled,
  signature: form.signature.trim() ? form.signature : null,
  show_images: form.inbox.show_images,
  show_unread_mail_indicator: form.inbox.show_unread_mail_indicator,
  confirm_link_urls: form.inbox.confirm_link_urls,
})

const applyVacation = (
  form: EmailSettingsForm,
  vacation: EmailVacationSettings,
): EmailSettingsForm => ({
  ...form,
  advanced: {
    automatic_replies_enabled: vacation.enabled,
    from_datetime: vacation.startTime ?? form.advanced.from_datetime,
    to_datetime: vacation.endTime ?? form.advanced.to_datetime,
    subject: vacation.subject,
    message: vacation.message,
  },
})

const applySendAs = (
  form: EmailSettingsForm,
  sendAs: EmailSendAsSettings,
): EmailSettingsForm => {
  const signature = sendAs.signature ?? ''
  return {
    ...form,
    display_name: sendAs.displayName ?? '',
    signature,
    signature_enabled: Boolean(signature.trim()),
  }
}

const toVacationPayload = (form: EmailSettingsForm): EmailVacationSettings => ({
  enabled: form.advanced.automatic_replies_enabled,
  subject: form.advanced.subject,
  message: form.advanced.message,
  startTime: form.advanced.from_datetime || null,
  endTime: form.advanced.to_datetime || null,
})

const toSendAsPayload = (
  form: EmailSettingsForm,
): { displayName: string; signature: string } => ({
  displayName: form.display_name.trim(),
  signature: form.signature_enabled ? form.signature : '',
})

const hasSignatureFieldsChanged = (
  current: EmailSettingsForm,
  baseline: EmailSettingsForm,
): boolean =>
  current.display_name !== baseline.display_name ||
  current.signature !== baseline.signature ||
  current.signature_enabled !== baseline.signature_enabled

const getEmailVacationValidationError = (
  advanced: EmailAdvancedSettings,
): string | null => {
  if (!advanced.automatic_replies_enabled) return null

  const { from_datetime, to_datetime } = advanced
  if (!from_datetime || !to_datetime) return null

  const start = new Date(from_datetime).getTime()
  const end = new Date(to_datetime).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return null

  if (end <= start) {
    return 'The end date cannot be earlier than the start date.'
  }

  return null
}

export interface IEmailSettingsHooks {
  tab: EmailTab
  setTab: (tab: EmailTab) => void
  data: EmailSettingsForm
  hasChanges: boolean
  loading: boolean
  dataReady: boolean
  handleChange: <K extends keyof EmailSettingsForm>(
    key: K,
    value: EmailSettingsForm[K],
  ) => void
  handleInboxChange: <K extends keyof EmailInboxSettings>(
    key: K,
    value: EmailInboxSettings[K],
  ) => void
  handleAdvancedChange: <K extends keyof EmailAdvancedSettings>(
    key: K,
    value: EmailAdvancedSettings[K],
  ) => void
  handleRemoveTag: (id: string) => Promise<void>
  handleCreateTag: (name: string, color: string) => Promise<void>
  handleUpdateTag: (id: string, name: string, color: string) => Promise<void>
  handleReset: () => void
  handleSave: () => Promise<void>
  handleGoogleMailboxConnected: () => Promise<void>
  handleGoogleMailboxDisconnected: () => Promise<void>
  setHeaderButtons: ReturnType<typeof useSettingsHeader>['setHeaderButtons']
}

const useEmailSettings = (): IEmailSettingsHooks => {
  const { setHeaderButtons } = useSettingsHeader()
  const { showToast } = useNotificationToast()

  const dispatch = useAppDispatch()
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )

  const [tab, setTab] = useState<EmailTab>('general')
  const [savedBaseline, setSavedBaseline] =
    useState<EmailSettingsForm>(DEFAULT_SETTINGS)
  const [data, setData] = useState<EmailSettingsForm>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const loadGen = useRef(0)

  useClinicChange(selectedClinicId ?? '', () => {
    loadGen.current += 1
    setDataReady(false)
    setLoading(true)
  })

  const hasChanges = useMemo(
    () => !areValuesEqual(flattenForm(data), flattenForm(savedBaseline)),
    [data, savedBaseline],
  )

  useSettingsSubpageLoading(!dataReady)

  const reloadSettings = useCallback(async (): Promise<void> => {
    if (!isEmailBackendEnabled()) return

    const [settings, labels] = await Promise.all([
      requestEmailSettings(),
      requestEmailLabels(),
    ])

    setData((prev) => ({
      ...(settings ? applySettings(prev, settings) : prev),
      tags: labels,
    }))
    setSavedBaseline((prev) => ({
      ...(settings ? applySettings(prev, settings) : prev),
      tags: labels,
    }))

    try {
      const vacation = await requestEmailVacation()
      if (vacation) {
        setData((prev) => applyVacation(prev, vacation))
        setSavedBaseline((prev) => applyVacation(prev, vacation))
      }
    } catch {
      // Automatic replies require a connected mailbox; keep defaults otherwise.
    }

    try {
      const sendAs = await requestEmailSendAs()
      if (sendAs) {
        setData((prev) => applySendAs(prev, sendAs))
        setSavedBaseline((prev) => applySendAs(prev, sendAs))
      }
    } catch {
      // Signature requires a connected mailbox; keep stored settings otherwise.
    }
  }, [])

  const syncFromGoogleMailbox = useCallback(async (): Promise<void> => {
    if (!isEmailBackendEnabled()) return
    try {
      const status = await requestGoogleWorkspaceStatus()
      if (!status?.isConnected) return
      await requestImportGoogleBackedEmailSettings()
    } catch {
      // Best-effort sync when opening settings with an existing connection.
    }
  }, [])

  useEffect(() => {
    const gen = ++loadGen.current
    setDataReady(false)
    setData(DEFAULT_SETTINGS)
    setSavedBaseline(DEFAULT_SETTINGS)
    void (async () => {
      setLoading(true)
      try {
        await syncFromGoogleMailbox()
        if (loadGen.current !== gen) return
        await reloadSettings()
      } catch {
        // Keep defaults if settings cannot be loaded.
      } finally {
        if (loadGen.current === gen) {
          setLoading(false)
          setDataReady(true)
        }
      }
    })()
  }, [reloadSettings, syncFromGoogleMailbox, selectedClinicId])

  const handleGoogleMailboxConnected = useCallback(async (): Promise<void> => {
    await reloadSettings()
  }, [reloadSettings])

  const handleGoogleMailboxDisconnected =
    useCallback(async (): Promise<void> => {
      dispatch(resetEmailState())
      await reloadSettings()
      setData((prev) => ({
        ...prev,
        profile_picture_url: null,
        display_name: '',
        signature: '',
        signature_enabled: false,
        tags: [],
        advanced: DISCONNECTED_ADVANCED_SETTINGS,
      }))
      setSavedBaseline((prev) => ({
        ...prev,
        profile_picture_url: null,
        display_name: '',
        signature: '',
        signature_enabled: false,
        tags: [],
        advanced: DISCONNECTED_ADVANCED_SETTINGS,
      }))
    }, [dispatch, reloadSettings])

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  const handleChange = useCallback(
    <K extends keyof EmailSettingsForm>(
      key: K,
      value: EmailSettingsForm[K],
    ): void => {
      setData((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const handleInboxChange = useCallback(
    <K extends keyof EmailInboxSettings>(
      key: K,
      value: EmailInboxSettings[K],
    ): void => {
      setData((prev) => ({
        ...prev,
        inbox: { ...prev.inbox, [key]: value },
      }))
    },
    [],
  )

  const handleAdvancedChange = useCallback(
    <K extends keyof EmailAdvancedSettings>(
      key: K,
      value: EmailAdvancedSettings[K],
    ): void => {
      setData((prev) => ({
        ...prev,
        advanced: { ...prev.advanced, [key]: value },
      }))
    },
    [],
  )

  const handleRemoveTag = useCallback(
    async (id: string): Promise<void> => {
      const removeLocally = (form: EmailSettingsForm): EmailSettingsForm => ({
        ...form,
        tags: form.tags.filter((tag) => tag.id !== id),
      })
      try {
        if (isEmailBackendEnabled()) {
          await requestDeleteEmailLabel(id)
        }
        setData(removeLocally)
        setSavedBaseline(removeLocally)
      } catch (error) {
        showToast(
          createFailedToast(
            'Email labels',
            getEmailMutationErrorMessage(error),
          ),
        )
      }
    },
    [showToast],
  )

  const handleCreateTag = useCallback(
    async (name: string, color: string): Promise<void> => {
      const trimmed = name.trim()
      if (!trimmed) return
      try {
        const tag = isEmailBackendEnabled()
          ? await requestCreateEmailLabel({ name: trimmed, color })
          : { id: `l-${Date.now()}`, name: trimmed, color }
        const addLocally = (form: EmailSettingsForm): EmailSettingsForm => ({
          ...form,
          tags: [tag, ...form.tags],
        })
        setData(addLocally)
        setSavedBaseline(addLocally)
        showToast(
          createSuccessToast('Email labels', `"${tag.name}" was added.`),
        )
      } catch (error) {
        showToast(
          createFailedToast(
            'Email labels',
            getEmailMutationErrorMessage(error),
          ),
        )
      }
    },
    [showToast],
  )

  const handleUpdateTag = useCallback(
    async (id: string, name: string, color: string): Promise<void> => {
      const trimmed = name.trim()
      if (!trimmed) return
      try {
        const tag = isEmailBackendEnabled()
          ? await requestUpdateEmailLabel(id, { name: trimmed, color })
          : { id, name: trimmed, color }
        const updateLocally = (form: EmailSettingsForm): EmailSettingsForm => ({
          ...form,
          tags: form.tags.map((existing) =>
            existing.id === id ? { ...existing, ...tag } : existing,
          ),
        })
        setData(updateLocally)
        setSavedBaseline(updateLocally)
        showToast(
          createSuccessToast('Email labels', `"${tag.name}" was updated.`),
        )
      } catch (error) {
        showToast(
          createFailedToast(
            'Email labels',
            getEmailMutationErrorMessage(error),
          ),
        )
      }
    },
    [showToast],
  )

  const handleReset = useCallback((): void => {
    setData({ ...savedBaseline })
  }, [savedBaseline])

  const handleSave = useCallback(async (): Promise<void> => {
    setLoading(true)
    const signatureChanged = hasSignatureFieldsChanged(data, savedBaseline)
    const advancedChanged =
      JSON.stringify(data.advanced) !== JSON.stringify(savedBaseline.advanced)

    try {
      if (isEmailBackendEnabled()) {
        const saved = await requestUpdateEmailSettings(toSettingsPayload(data))
        let next = applySettings(data, saved)

        if (signatureChanged) {
          const sendAs = await requestUpdateEmailSendAs(toSendAsPayload(data))
          next = applySendAs(next, sendAs)
        }

        if (advancedChanged) {
          const vacationError = getEmailVacationValidationError(data.advanced)
          if (vacationError) {
            showToast(createFailedToast('Automatic replies', vacationError))
            return
          }

          const vacation = await requestUpdateEmailVacation(
            toVacationPayload(data),
          )
          next = applyVacation(next, vacation)
        }

        setData((prev) => ({
          ...applySettings(prev, saved),
          display_name: next.display_name,
          signature: next.signature,
          signature_enabled: next.signature_enabled,
          advanced: next.advanced,
        }))
        setSavedBaseline(next)
      } else {
        await new Promise((resolve) => setTimeout(resolve, 200))
        setSavedBaseline({ ...data })
      }
      showToast(createSuccessToast('Email settings', 'Changes saved.'))
    } catch (error) {
      const title =
        signatureChanged && !advancedChanged
          ? 'Email signature'
          : advancedChanged && !signatureChanged
            ? 'Automatic replies'
            : 'Email settings'
      const message =
        signatureChanged || advancedChanged
          ? getGoogleWorkspaceErrorMessage(error)
          : getEmailSettingsErrorMessage(error)
      showToast(createFailedToast(title, message))
    } finally {
      setLoading(false)
    }
  }, [data, savedBaseline, showToast])

  return {
    tab,
    setTab,
    data,
    hasChanges,
    loading,
    dataReady,
    handleChange,
    handleInboxChange,
    handleAdvancedChange,
    handleRemoveTag,
    handleCreateTag,
    handleUpdateTag,
    handleReset,
    handleSave,
    handleGoogleMailboxConnected,
    handleGoogleMailboxDisconnected,
    setHeaderButtons,
  }
}

export { LOADING_CONTAINER }
export default useEmailSettings
